import {
  AlignmentType,
  BorderStyle,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
  convertMillimetersToTwip,
} from "docx";
import { ChecklistCriteria, CriteriaInstance } from "types/Checklist";
import { CellTextParagraphs } from "types/WordChecklist";
import { useChecklistCellMargins } from "./useChecklistCellMargins";
import { useTokenTextRun } from "./useTokenTextRun";
import { useWordFontSize } from "./useWordFontSize";
import { useShouldIncludeChecklistCriteriaCheckmark } from "../useShouldIncludeChecklistCriteriaCheckmark";

export const useWordChecklistCriteriaGenerator = (
  criteriaInstance: CriteriaInstance,
  allCriteria: ChecklistCriteria[],
  numParagraphs: number,
  numParagraphColumns: number,
  cellTextToParagraphs: CellTextParagraphs[],
  cellAlignment: any = AlignmentType.LEFT,
  color: string = "000000",
  hideScores: boolean = false,
  includeCriteriaCheckmarks: boolean = false,
  isStudentMode: boolean = false,
  useGoogleDocsCompatibleLayout: boolean = false
) => {
  const allRows = [];
  const normalizedColor = color.replace("#", "");

  const criteria = criteriaInstance.checklistCriteriaId
    ? useGetCriteriaFromId(allCriteria, criteriaInstance.checklistCriteriaId)
    : undefined;

  if (criteria || criteriaInstance.isCustom) {
    const cellMargins = useChecklistCellMargins();
    const fontSize = useWordFontSize();
    const criteriaLabel =
      criteria?.label ?? criteriaInstance.labelOverride ?? "N/A";

    let label = [
      new TextRun({
        text: criteriaLabel,
        size: fontSize,
        color: normalizedColor,
        strike: criteriaInstance.ignore == true,
      }),
    ];

    for (const cellTextParagraphsInstance of cellTextToParagraphs) {
      const cellText = cellTextParagraphsInstance.text
        .replace(/\n/g, "") // Remove newline characters
        .toLowerCase() // Convert to lowercase
        .replace(/\s+/g, " ") // Replace multiple spaces with a single space
        .trim(); // Remove leading and trailing spaces

      const newCriteriaLabel = criteriaLabel
        .replace(/\n/g, "") // Remove newline characters
        .toLowerCase() // Convert to lowercase
        .replace(/\s+/g, " ") // Replace multiple spaces with a single space
        .trim(); // Remove leading and trailing spaces

      if (
        cellText.includes(newCriteriaLabel) ||
        newCriteriaLabel.includes(cellText)
      ) {
        label = [];
        for (const paragraph of cellTextParagraphsInstance.paragraphs) {
          for (const token of paragraph.tokens) {
            const textRun = useTokenTextRun(token);
            label.push(textRun);
          }

          label.push(new TextRun({ text: " " }));
        }
      }
    }

    const criteriaRow = new TableRow({
      children: [
        new TableCell({
          margins: cellMargins,
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: useShouldIncludeChecklistCriteriaCheckmark(
                    criteriaInstance,
                    includeCriteriaCheckmarks,
                    isStudentMode
                  )
                    ? "✓"
                    : "",
                  size: fontSize,
                }),
              ],
              alignment: AlignmentType.CENTER,
            }),
          ],
        }),
        new TableCell({
          margins: cellMargins,
          children: [
            new Paragraph({
              alignment: cellAlignment,
              children: label,
            }),
          ],
        }),
      ],
    });

    if (
      useGoogleDocsCompatibleLayout &&
      criteriaInstance.evaluationMethod == "paragraph" &&
      criteriaInstance.paragraphPositionsData &&
      criteriaInstance.paragraphPositionsData.length > 1
    ) {
      const scoreLabels = criteriaInstance.paragraphPositionsData.map(
        (paragraphPositionData) => {
          const paragraphPoints =
            paragraphPositionData.manualScore ??
            paragraphPositionData.score ??
            0;

          if (hideScores || criteriaInstance.pointsPerInstance == 0) return "";

          return criteriaInstance.pointsPerInstance
            ? `${paragraphPoints}/${criteriaInstance.pointsPerInstance}`
            : paragraphPoints.toString();
        }
      );

      criteriaRow.addChildElement(
        new TableCell({
          margins: cellMargins,
          verticalAlign: VerticalAlign.CENTER,
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  text:
                    criteriaInstance.ignore == true || hideScores
                      ? ""
                      : scoreLabels.join(" | "),
                  size: fontSize,
                  color: normalizedColor,
                }),
              ],
            }),
          ],
        })
      );
    } else if (
      criteriaInstance.evaluationMethod == "paragraph" &&
      criteriaInstance.paragraphPositionsData &&
      criteriaInstance.paragraphPositionsData.length > 1
    ) {
      const numEarnedPointsParagraphs =
        criteriaInstance.paragraphPositionsData.length;

      let columnSpan = Math.floor(
        numParagraphColumns / numEarnedPointsParagraphs
      );
      let totalColumns = 0;
      let index = 0;

      const scoreCells: TableCell[] = [];

      for (const paragraphPositionData of criteriaInstance.paragraphPositionsData) {
        const paragraphPoints =
          paragraphPositionData.manualScore ?? paragraphPositionData.score ?? 0;
        totalColumns += columnSpan;

        // If the current key is the last keym and the total columns isn't yet equal to numParagraphs
        // Then we need to set columnSpan to the difference between numParagraphs and totalColumns
        if (
          index == numEarnedPointsParagraphs - 1 &&
          totalColumns != numParagraphColumns
        ) {
          columnSpan += numParagraphColumns - totalColumns;
        }

        var scoreCellText = "0";

        if (paragraphPoints) {
          scoreCellText = paragraphPoints.toString();
        }

        if (criteriaInstance.pointsPerInstance) {
          scoreCellText += `/${criteriaInstance.pointsPerInstance}`;
        }

        if (criteriaInstance.pointsPerInstance == 0 || hideScores) {
          scoreCellText = "";
        }

        var scoreCell = new TableCell({
          margins: cellMargins,
          borders: {
            bottom: {
              style: BorderStyle.NONE,
            },
            top: {
              style: BorderStyle.NONE,
            },
            left: {
              style: index == 0 ? BorderStyle.NONE : BorderStyle.INSET,
              size: 2,
            },
            right: {
              style: BorderStyle.NONE,
            },
          },
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  text: criteriaInstance.ignore != true ? scoreCellText : "",
                  size: fontSize,
                  color: normalizedColor,
                  strike: criteriaInstance.ignore == true,
                }),
              ],
            }),
          ],
        });

        scoreCells.push(scoreCell);

        index++;
      }

      let scoreCellIndex = 0;

      const columnWidths = [];
      const columnWidth = 3212 / scoreCells.length;

      while (scoreCellIndex < scoreCells.length) {
        columnWidths.push(columnWidth);
        scoreCellIndex++;
      }

      const scoreTable = new Table({
        layout: TableLayoutType.AUTOFIT,
        borders: {
          bottom: {
            style: BorderStyle.NONE,
          },
          top: {
            style: BorderStyle.NONE,
          },
          left: {
            style: BorderStyle.NONE,
          },
          right: {
            style: BorderStyle.NONE,
          },
          insideHorizontal: {
            style: BorderStyle.NONE,
          },
          insideVertical: {
            style: BorderStyle.NONE,
          },
        },
        columnWidths: columnWidths,
        rows: [
          new TableRow({
            children: scoreCells,
            // height: {
            //   rule: HeightRule.AUTO,
            //   value: `100mm`,
            // },
          }),
        ],
        margins: {
          top: convertMillimetersToTwip(0),
          bottom: convertMillimetersToTwip(0),
          right: convertMillimetersToTwip(0),
          left: convertMillimetersToTwip(0),
        },
        width: {
          size: 100,
          type: WidthType.PERCENTAGE,
        },
      });

      const scoreCellWrapper = new TableCell({
        margins: {
          top: convertMillimetersToTwip(0),
          bottom: convertMillimetersToTwip(0),
          right: convertMillimetersToTwip(0),
          left: convertMillimetersToTwip(0),
        },
        borders: {
          bottom: {
            style: BorderStyle.INSET,
            size: 2,
          },
          top: {
            style: BorderStyle.INSET,
            size: 2,
          },
          left: {
            style: BorderStyle.INSET,
            size: 2,
          },
          right: {
            style: BorderStyle.INSET,
            size: 2,
          },
        },
        verticalAlign: VerticalAlign.CENTER,
        children: [scoreTable],
      });

      criteriaRow.addChildElement(scoreCellWrapper);
    } else {
      var scoreCellText = "";

      if (!criteriaInstance.earnedPoints) {
        criteriaInstance.earnedPoints = 0;
      }

      if (criteria && criteria.showPointsPlus) {
        scoreCellText = `+${criteriaInstance.earnedPoints.toString()}`;
      } else if (
        criteriaInstance.earnedPoints != 0 ||
        criteriaInstance.maxPoints != undefined
      ) {
        scoreCellText = criteriaInstance.earnedPoints.toString();
      }

      if (criteriaInstance.maxPoints) {
        scoreCellText += `/${criteriaInstance.maxPoints}`;
      }

      if (criteriaInstance.maxPoints == 0 || hideScores) {
        scoreCellText = "";
      }

      var scoreCell = new TableCell({
        margins: cellMargins,
        verticalAlign: VerticalAlign.CENTER,
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: criteriaInstance.ignore != true ? scoreCellText : "",
                size: fontSize,
                color: normalizedColor,
              }),
            ],
          }),
        ],
      });
      criteriaRow.addChildElement(scoreCell);
    }

    allRows.push(criteriaRow);
  }

  return allRows;
};
